/**
 * Notification Center Component
 *
 * Full-page view of all notifications with filtering, grouping,
 * and management capabilities.
 *
 * Loads notifications from both the UI store (transient) and Supabase
 * (persistent), merging them with deduplication — same pattern as
 * NotificationBell.
 *
 * Usage:
 * ```tsx
 * <NotificationCenter />
 * ```
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import type { MouseEvent } from 'react';
import { PageHeader } from './PageHeader';
import { PhoneAlertPreferences } from './PhoneAlertPreferences';
import { useDismissedTaskNotificationIds } from '../../hooks/useDismissedTaskNotificationIds';
import {
  Bell,
  Clock,
  CheckCircle,
  AlertTriangle,
  Info,
  Filter,
  Trash2,
  X,
  ChevronRight,
  Loader2,
  Settings,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { usePrioritizationTaskStore } from '../../stores/prioritization/prioritizationTaskStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import {
  loadNotificationsForUser,
  loadNotificationHistoryForUser,
  markAllNotificationsReadInSupabase,
  dismissNotificationInSupabase,
  type StoredNotification,
} from '../../lib/notificationService';
import { markNotificationUnread, undismissNotification } from '../../lib/reversals/reversalsService';
import { Button } from '../ui/Button';
import { btnPrimary, btnSecondary } from '../mono/primitives';
import { useSessionNotificationJoinability } from '../../hooks/useSessionNotificationJoinability';
import {
  resolveSessionNotificationAffordance,
  sessionIdFromNotification,
} from '../../lib/sessionNotificationAffordance';
import {
  countActionableUnread,
  isTaskPendingNotification,
  isUiNotificationBadgeEligible,
  type NotificationPriority,
} from '../../lib/notificationBadgeRules';

// ============================================================================
// Types
// ============================================================================

type InboxTab = 'active' | 'history';
type NotificationType = 'all' | 'tasks' | 'info' | 'success' | 'warning' | 'error';

interface NotificationItem {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'task';
  title: string;
  message: string;
  timestamp: Date;
  read?: boolean;
  priority?: NotificationPriority;
  actionUrl?: string;
  actionLabel?: string;
  /** Notification event slug — needed to tell a "Live now" row from any other. */
  event?: string;
  /** Session the row points at, when it points at one. */
  sessionId?: string;
  category?: string;
  isSupabase?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function NotificationCenter() {
  const [inboxTab, setInboxTab] = useState<InboxTab>('active');
  const [filter, setFilter] = useState<NotificationType>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [supabaseNotifications, setSupabaseNotifications] = useState<StoredNotification[]>([]);
  const [historyNotifications, setHistoryNotifications] = useState<StoredNotification[]>([]);
  const [showPrefs, setShowPrefs] = useState(false);

  // Get notifications from UI store
  const uiNotifications = useUIStore(state => state.notifications);
  const removeNotification = useUIStore(state => state.removeNotification);
  const clearNotifications = useUIStore(state => state.clearNotifications);
  const addNotification = useUIStore(state => state.addNotification);

  // Current user
  const userId = useUserStore(state => state.userId);
  const { dismissedTaskIds, dismissTaskNotification, dismissAllTaskNotifications } =
    useDismissedTaskNotificationIds(userId);

  // Get pending tasks for the current user
  const allTasks = usePrioritizationTaskStore(state => state.tasks);
  const pendingTasks = useMemo(() => {
    if (!userId) return [];
    return allTasks.filter((t) => isTaskPendingNotification(t, userId, dismissedTaskIds));
  }, [allTasks, userId, dismissedTaskIds]);

  const completedTasks = useMemo(() => {
    return allTasks.filter(t =>
      t.participantIds.includes(userId) &&
      t.responses.some(r => r.participantId === userId)
    );
  }, [allTasks, userId]);

  // -------------------------------------------------------------------------
  // Supabase notification loading
  // -------------------------------------------------------------------------

  const loadNotifications = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      if (inboxTab === 'history') {
        const history = await loadNotificationHistoryForUser(userId);
        setHistoryNotifications(history);
      } else {
        const notifications = await loadNotificationsForUser(userId);
        setSupabaseNotifications(notifications);
      }
    } catch (err) {
      console.error('[NotificationCenter] Error loading notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, inboxTab]);

  // Load on mount and when Active / History tab changes
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // -------------------------------------------------------------------------
  // Merge all notification sources (same pattern as NotificationBell)
  // -------------------------------------------------------------------------

  const allNotifications = useMemo(() => {
    const items: NotificationItem[] = [];
    const seenIds = new Set<string>();

    if (inboxTab === 'history') {
      historyNotifications.forEach((n) => {
        if (seenIds.has(n.id)) return;
        seenIds.add(n.id);
        items.push({
          id: n.id,
          type: n.payload.event?.includes('task') ? 'task' : 'info',
          title: n.payload.title,
          message: n.payload.body,
          timestamp: n.dismissedAt ?? n.createdAt,
          read: true,
          priority: n.payload.priority,
          actionUrl: n.payload.actionUrl,
          actionLabel: n.payload.actionLabel,
          event: n.payload.event,
          sessionId:
            sessionIdFromNotification({
              data: n.payload.data as Record<string, unknown> | null | undefined,
              actionUrl: n.payload.actionUrl,
            }) ?? undefined,
          category: 'system',
          isSupabase: true,
        });
      });
      return items.sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
    }

    // Supabase notifications first (persistent)
    supabaseNotifications.forEach(n => {
      if (seenIds.has(n.id)) return;
      seenIds.add(n.id);
      items.push({
        id: n.id,
        type: n.payload.event?.includes('task') ? 'task' : 'info',
        title: n.payload.title,
        message: n.payload.body,
        timestamp: n.createdAt,
        read: n.read,
        priority: n.payload.priority,
        actionUrl: n.payload.actionUrl,
        actionLabel: n.payload.actionLabel,
        event: n.payload.event,
        sessionId:
          sessionIdFromNotification({
            data: n.payload.data as Record<string, unknown> | null | undefined,
            actionUrl: n.payload.actionUrl,
          }) ?? undefined,
        category: 'system',
        isSupabase: true,
      });
    });

    // UI (transient) notifications
    uiNotifications.forEach(n => {
      if (seenIds.has(n.id)) return;
      seenIds.add(n.id);
      const eligible = isUiNotificationBadgeEligible(n.timestamp);
      items.push({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        timestamp: n.timestamp,
        read: !eligible,
        priority: 'normal',
        category: 'system',
        isSupabase: false,
      });
    });

    // A task is listed once: by the task_assigned row the database holds
    // (subject 'task'). It is finished when the task completes (migration 1053).

    // Sort newest first
    return items.sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [inboxTab, historyNotifications, supabaseNotifications, uiNotifications]);

  // "Live now" is the product's only urgent alert; resolve what each one points
  // at so a closed room stops advertising a Join button.
  const sessionJoinability = useSessionNotificationJoinability(
    inboxTab === 'history' ? historyNotifications : supabaseNotifications,
  );

  // -------------------------------------------------------------------------
  // Filter / group
  // -------------------------------------------------------------------------

  const filteredNotifications = useMemo(() => {
    if (filter === 'all') return allNotifications;
    if (filter === 'tasks') return allNotifications.filter(n => n.type === 'task');
    return allNotifications.filter(n => n.type === filter);
  }, [allNotifications, filter]);

  const groupedNotifications = useMemo(() => {
    const groups: Record<string, NotificationItem[]> = {};

    filteredNotifications.forEach(n => {
      const date = new Date(n.timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let key: string;
      if (date.toDateString() === today.toDateString()) {
        key = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        key = 'Yesterday';
      } else {
        key = date.toLocaleDateString(undefined, {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
        });
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(n);
    });

    return groups;
  }, [filteredNotifications]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const handleMarkAllRead = async () => {
    if (!supabaseNotifications.some(n => !n.read)) return;
    // One round trip through the RPC instead of one request per row — and,
    // unlike the old per-row loop, a failure here is shown instead of being
    // swallowed and quietly left as if every row had been marked read.
    const updated = await markAllNotificationsReadInSupabase();
    if (updated === null) {
      addNotification({
        type: 'error',
        title: 'Could not mark notifications read',
        message: 'Try again.',
        duration: 5000,
      });
      return;
    }
    setSupabaseNotifications(prev =>
      prev.map(n => ({ ...n, read: true, readAt: new Date() }))
    );
  };

  const handleClearAll = useCallback(async () => {
    const ids = supabaseNotifications.map((n) => n.id);
    const results = await Promise.all(
      ids.map(async (id) => ({ id, ok: await dismissNotificationInSupabase(id).catch(() => false) }))
    );
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    clearNotifications();
    dismissAllTaskNotifications(pendingTasks.map((t) => t.id));
    // Only drop the ones that actually dismissed — a row that was refused
    // stays visible instead of vanishing as if it had cleared.
    setSupabaseNotifications((prev) => prev.filter((n) => failedIds.has(n.id)));
    setSelectedIds(new Set());
    if (failedIds.size > 0) {
      addNotification({
        type: 'error',
        title: 'Some notifications were not cleared',
        message: `${failedIds.size} of ${ids.length} could not be dismissed. Try again.`,
        duration: 6000,
      });
    }
  }, [
    supabaseNotifications,
    clearNotifications,
    dismissAllTaskNotifications,
    addNotification,
    pendingTasks,
  ]);

  const handleDismiss = useCallback(
    async (id: string, e?: MouseEvent) => {
      e?.stopPropagation();
      e?.preventDefault();

      if (id.startsWith('task-')) {
        dismissTaskNotification(id);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        return true;
      }

      const supabaseNotif = supabaseNotifications.find((n) => n.id === id);
      if (supabaseNotif) {
        // Optimistic remove; restore the row if the server refuses, so a
        // failed dismiss never looks like it worked.
        setSupabaseNotifications((prev) => prev.filter((n) => n.id !== id));
        const ok = await dismissNotificationInSupabase(id).catch(() => false);
        if (!ok) {
          setSupabaseNotifications((prev) =>
            prev.some((n) => n.id === id) ? prev : [supabaseNotif, ...prev],
          );
          addNotification({
            type: 'error',
            title: 'Notification was not dismissed',
            message: 'The server refused the change. Try again.',
            duration: 6000,
          });
          return false;
        }
      }
      removeNotification(id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return true;
    },
    [dismissTaskNotification, removeNotification, supabaseNotifications, addNotification]
  );

  // Personal inbox undo (mig 746): recipient-scoped, no reason — the inbox is
  // the reader's own state, not a governance act. Local state changes only
  // after the server confirms.
  const handleMarkUnread = useCallback(
    async (id: string) => {
      const ok = await markNotificationUnread(id);
      if (ok) {
        setSupabaseNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: false } : n)));
      } else {
        addNotification({ type: 'error', title: 'Could not mark as unread', message: 'Try again.', duration: 5000 });
      }
    },
    [addNotification],
  );

  const handleRestore = useCallback(
    async (id: string) => {
      const ok = await undismissNotification(id);
      if (ok) {
        setHistoryNotifications((prev) => prev.filter((n) => n.id !== id));
      } else {
        addNotification({ type: 'error', title: 'Could not restore', message: 'Try again.', duration: 5000 });
      }
    },
    [addNotification],
  );

  const handleSelectAll = () => {
    if (selectedIds.size === filteredNotifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredNotifications.map(n => n.id)));
    }
  };

  const handleDeleteSelected = () => {
    selectedIds.forEach((id) => {
      void handleDismiss(id);
    });
    setSelectedIds(new Set());
  };

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };


  // -------------------------------------------------------------------------
  // Rendering helpers
  // -------------------------------------------------------------------------

  const getIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-[var(--green)]" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-[var(--mustard)]" />;
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-[var(--coral)]" />;
      case 'task':
        return <Clock className="h-5 w-5 text-[var(--text-ter)]" />;
      default:
        return <Info className="h-5 w-5 text-[var(--text-ter)]" />;
    }
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const unreadCount = countActionableUnread(allNotifications);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header — the shared page header every page uses (icon box, title, subtitle, actions). */}
      <PageHeader
        icon={<Bell className="h-4 w-4 text-[var(--text-ter)]" />}
        title="Notifications"
        mb=""
        subtitle={
          <>
            {allNotifications.length} notification{allNotifications.length !== 1 ? 's' : ''}
            {unreadCount > 0 && (
              <span className="text-[var(--text-ter)] ml-1">({unreadCount} unread)</span>
            )}
            {pendingTasks.length > 0 && (
              <span className="text-[var(--text-ter)] ml-1">
                · {pendingTasks.length} pending task{pendingTasks.length !== 1 ? 's' : ''}
              </span>
            )}
            {isLoading && (
              <Loader2 className="inline ml-2 h-4 w-4 text-[var(--text-ter)] animate-spin" />
            )}
          </>
        }
        actions={
          <>
          {inboxTab === 'active' && unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              {...btnSecondary()}
            >
              <CheckCircle className="h-3.5 w-3.5" aria-hidden />
              Mark all read
            </button>
          )}
          {inboxTab === 'active' && selectedIds.size > 0 && (
            <button
              type="button"
              onClick={handleDeleteSelected}
              {...btnSecondary()}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              Delete ({selectedIds.size})
            </button>
          )}
          {inboxTab === 'active' && (
            <button
              type="button"
              onClick={() => void handleClearAll()}
              {...btnSecondary()}
            >
              Clear All
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowPrefs(v => !v)}
            {...btnSecondary(showPrefs ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : '')}
            aria-pressed={showPrefs}
          >
            <Settings className="h-3.5 w-3.5" aria-hidden />
            Alerts
          </button>
          </>
        }
      />

      {/* Alerts: opened by the button above, drawn under it */}
      {showPrefs && <PhoneAlertPreferences onClose={() => setShowPrefs(false)} />}

      {/* Active / History */}
      <div className="flex items-center gap-2">
        {([
          { id: 'active' as const, label: 'Active' },
          { id: 'history' as const, label: 'History' },
        ]).map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setInboxTab(tab.id);
              setSelectedIds(new Set());
            }}
            {...(inboxTab === tab.id ? btnPrimary() : btnSecondary())}
            aria-pressed={inboxTab === tab.id}
          >
            {tab.label}
          </button>
        ))}
        {inboxTab === 'history' && (
          <p className="text-xs text-[var(--text-ter)] ml-2">
            Dismissed notifications — read-only
          </p>
        )}
      </div>

      {/* Filters (active inbox only) */}
      {inboxTab === 'active' && (
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-[var(--text-ter)]" />
        {(['all', 'tasks', 'info', 'success', 'warning', 'error'] as NotificationType[]).map(type => (
          <button
            key={type}
            onClick={() => setFilter(type)}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
              filter === type
                ? 'bg-primary-100 text-primary-700'
                : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }`}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
            {type === 'tasks' && pendingTasks.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-[var(--slate)] text-white rounded-full">
                {pendingTasks.length}
              </span>
            )}
          </button>
        ))}
      </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[var(--slate-dim)] p-4">
          <div className="flex items-center gap-3">
            <Clock className="h-8 w-8 text-[var(--text-ter)]" />
            <div>
              <p className="text-2xl font-bold text-[var(--text-sec)]">{pendingTasks.length}</p>
              <p className="text-sm text-[var(--text-sec)]">Pending Tasks</p>
            </div>
          </div>
        </div>

        <div className="bg-[var(--green-dim)] p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-[var(--green)]" />
            <div>
              <p className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{completedTasks.length}</p>
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">Completed Tasks</p>
            </div>
          </div>
        </div>

        <div className="bg-[var(--surface)] p-4">
          <div className="flex items-center gap-3">
            <Bell className="h-8 w-8 text-[var(--text-ter)]" />
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{uiNotifications.length}</p>
              <p className="text-sm text-[var(--text-sec)]">System Notifications</p>
            </div>
          </div>
        </div>
      </div>

      {/* Notification List */}
      <div className="bg-[var(--bg-raised)] border">
        {/* Select All Header (active only) */}
        {inboxTab === 'active' && filteredNotifications.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-3 border-b bg-[var(--surface)]">
            <input
              type="checkbox"
              checked={selectedIds.size === filteredNotifications.length && filteredNotifications.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 rounded border-[var(--rule)] text-primary-600"
            />
            <span className="text-sm text-[var(--text-sec)]">
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : 'Select all'
              }
            </span>
          </div>
        )}

        {/* Empty State */}
        {filteredNotifications.length === 0 && (
          <div className="px-4 py-12 text-center">
            <Bell className="h-12 w-12 mx-auto mb-4 text-[var(--text-ter)]" />
            <p className="text-[var(--text-sec)] font-medium">
              {inboxTab === 'history' ? 'No dismissed notifications' : 'No notifications'}
            </p>
            <p className="text-sm text-[var(--text-ter)] mt-1">
              {inboxTab === 'history'
                ? 'Dismissed items appear here'
                : filter !== 'all'
                  ? `No ${filter} notifications to show`
                  : "You're all caught up!"
              }
            </p>
          </div>
        )}

        {/* Grouped Notifications */}
        {Object.entries(groupedNotifications).map(([date, notifications]) => (
          <div key={date}>
            <div className="px-4 py-2 bg-[var(--surface)] border-b">
              <span className="text-sm font-medium text-[var(--text-sec)]">{date}</span>
            </div>

            <div className="divide-y">
              {notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-4 px-4 py-4 hover:bg-[var(--surface)] transition-colors ${
                    inboxTab === 'active' && !notification.read ? 'bg-[var(--slate-dim)]/30' : ''
                  }`}
                >
                  {inboxTab === 'active' && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(notification.id)}
                      onChange={() => toggleSelect(notification.id)}
                      className="mt-1 h-4 w-4 rounded border-[var(--rule)] text-primary-600"
                    />
                  )}

                  <div className="flex-shrink-0 mt-0.5">
                    {getIcon(notification.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="flex items-center gap-1.5 font-medium text-[var(--text-pri)]">
                          <span>{notification.title}</span>
                          {notification.priority === 'urgent' && (
                            <span className="shrink-0 rounded-sm bg-[var(--coral-dim)] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--coral)]">
                              Urgent
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-[var(--text-sec)] mt-0.5">{notification.message}</p>
                      </div>
                      <span className="flex-shrink-0 text-sm text-[var(--text-ter)]">
                        {formatTime(notification.timestamp)}
                      </span>
                    </div>

                    {(() => {
                      const affordance = resolveSessionNotificationAffordance({
                        event: notification.event,
                        actionUrl: notification.actionUrl,
                        actionLabel: notification.actionLabel,
                        sessionId: notification.sessionId,
                        joinability: notification.sessionId
                          ? sessionJoinability[notification.sessionId]
                          : undefined,
                      });
                      if (!affordance) return null;
                      return (
                        <div className="flex items-center gap-2 mt-2">
                          {affordance.statusChip && (
                            <span className="rounded-sm bg-[var(--surface)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--text-ter)]">
                              {affordance.statusChip}
                            </span>
                          )}
                          <Link
                            to={affordance.actionUrl}
                            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                          >
                            {affordance.actionLabel}
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </div>
                      );
                    })()}
                  </div>

                  {inboxTab === 'active' && notification.isSupabase && notification.read && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleMarkUnread(notification.id)}
                      className="flex-shrink-0 text-xs"
                    >
                      Mark unread
                    </Button>
                  )}
                  {inboxTab === 'history' && notification.isSupabase && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleRestore(notification.id)}
                      className="flex-shrink-0 text-xs"
                    >
                      Restore to inbox
                    </Button>
                  )}
                  {inboxTab === 'active' && (
                    <button
                      type="button"
                      onClick={(ev) => void handleDismiss(notification.id, ev)}
                      className="flex-shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded"
                      aria-label="Dismiss notification"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

export default NotificationCenter;
